<script lang="ts" setup>
import { AbcFloatingContainer, AbcHeading } from "@ordbogen-npm/abc-dsk";
import { onMounted } from "vue";
import { useI18n } from "vue-i18n";

import TgTooltip from "@/abc-dsk/components/Tooltip/TgTooltip.vue";
import { useVocabAchievements } from "@/common/composables/use-vocab-achievements";
import { useVocabBadges } from "@/common/composables/use-vocab-badges";
import BadgeRedemptionModal from "@/components/modals/vocabularyTrainer/BadgeRedemptionModal.vue";
import AchievementBadge from "@/components/vocabularyTrainer/AchievementBadge.vue";
import AchievementItem from "@/components/vocabularyTrainer/AchievementItem.vue";

const { t } = useI18n();

const {
  initialize,
  achievementHasMultipleTiers,
  achievements,
  openedBadges,
  unopenedAchievements,
  ongoingAchievements,
  openedNormalAchievements,
  openedCampaignAchievements
} = useVocabAchievements();

const {
  badge: redeemBadge,
  isRedemptionModalOpen,
  onRedeemClick,
  onCloseRedemptionModal,
  onBadgeRedeem
} = useVocabBadges(achievements);

onMounted(() => {
  initialize();
});
</script>

<template>
  <main class="vocab-achievements container">
    <AbcFloatingContainer class="vocab-achievements__container">
      <section class="vocab-achievements__section vocab-achievements__header-section">
        <AbcHeading size="md" tag="h1" class="mb-2">{{
          t("vocab.achievements.header")
        }}</AbcHeading>
        <p class="vocab-achievements__header-description">
          {{ t("vocab.achievements.description") }}
        </p>
      </section>

      <section class="vocab-achievements__section">
        <section v-if="openedBadges.length">
          <p class="vocab-achievements__section-header">
            {{ t("vocab.achievements.badgesHeader") }}
          </p>

          <div class="vocab-achievements__badges">
            <TgTooltip
              v-for="(badge, index) in openedBadges"
              :key="index"
              :text="badge.displayName"
              :margin-bottom="10"
            >
              <AchievementBadge class="vocab-achievements__badge" :badge="badge" />
            </TgTooltip>
          </div>
        </section>
        <section v-if="unopenedAchievements.length">
          <p class="vocab-achievements__section-header">
            {{ t("vocab.achievements.unopenedAchievementsHeader") }}
          </p>

          <div class="vocab-achievements__achievement-item-grid">
            <AchievementItem
              v-for="achievement in unopenedAchievements"
              :key="`${achievement.slug}-${achievement.index}`"
              :achievement="achievement"
              :show-steps-in-name="achievementHasMultipleTiers(achievement)"
              @redeem-click="onRedeemClick(achievement)"
            />
          </div>
        </section>
        <section>
          <p class="vocab-achievements__section-header">
            {{ t("vocab.achievements.ongoingAchievementsHeader") }}
          </p>

          <div class="vocab-achievements__achievement-item-grid">
            <AchievementItem
              v-for="achievement in ongoingAchievements"
              :key="`${achievement.slug}-${achievement.index}`"
              :achievement="achievement"
              :show-steps-in-name="achievementHasMultipleTiers(achievement)"
            />
          </div>
        </section>
        <section v-if="openedNormalAchievements.length">
          <p class="vocab-achievements__section-header">
            {{ t("vocab.achievements.openedNormalAchievementsHeader") }}
          </p>

          <div class="vocab-achievements__achievement-item-grid">
            <AchievementItem
              v-for="achievement in openedNormalAchievements"
              :key="`${achievement.slug}-${achievement.index}`"
              :achievement="achievement"
              :show-steps-in-name="achievementHasMultipleTiers(achievement)"
            />
          </div>
        </section>
        <section v-if="openedCampaignAchievements.length">
          <p class="vocab-achievements__section-header">
            {{ t("vocab.achievements.openedCampaignAchievementsHeader") }}
          </p>

          <div class="vocab-achievements__achievement-item-grid">
            <AchievementItem
              v-for="achievement in openedCampaignAchievements"
              :key="`${achievement.slug}-${achievement.index}`"
              :achievement="achievement"
              :show-steps-in-name="achievementHasMultipleTiers(achievement)"
            />
          </div>
        </section>
      </section>
    </AbcFloatingContainer>
    <BadgeRedemptionModal
      :is-open="isRedemptionModalOpen"
      :badge="redeemBadge"
      @close="onCloseRedemptionModal()"
      @redeem="onBadgeRedeem()"
    />
  </main>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.vocab-achievements {
  &__container {
    margin-bottom: 72px;
  }

  &__section {
    padding: 22px 16px;

    #media-breakpoint-up(md, {
    padding: 36px 30px;
  });

    &-header {
      margin-bottom: 14px;
      color: rgb(@theme-colors[@grey][base]);
      font-size: 0.875rem;
      font-weight: 400;
      line-height: 1.25rem;
    }
  }

  &__header {
    &-section {
      border-bottom: 1px solid rgb(@theme-colors[@grey][50]);
    }

    &-description {
      margin-bottom: 0;
      color: rgb(@theme-colors[@grey][base]);
    }
  }

  &__achievement-item-grid {
    display: grid;
    gap: 10px;
    grid-template-columns: 1fr;
    margin-bottom: 30px;

    #media-breakpoint-up(md, {
      grid-template-columns: 1fr 1fr;
      gap: 30px;
      margin-bottom: 40px;
    });
  }

  &__badges {
    display: flex;
    margin-bottom: 30px;
    padding: 18px 12px;
    overflow-x: scroll;
    border: 1px solid rgb(@theme-colors[@grey][50]);
    border-radius: 8px;
    gap: 16px;

    #media-breakpoint-up(md, {
      margin-bottom: 40px;
      padding: 28px 22px;
    });

    // Snap scroll on touch devices
    @media (hover: none) and (pointer: coarse) {
      scroll-snap-type: x proximity;

      .vocab-achievements__badge {
        scroll-snap-align: end;
      }
    }

    .vocab-achievements__badge {
      transition: box-shadow 100ms ease-in-out;

      &:hover {
        box-shadow: 5px 5px 10px rgba(0, 0, 0, 0.08);
      }
    }
  }
}
</style>
